import { useMutation, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/lib/queryKeys';
import { updateQueueStatus } from './api';
import type { QueueItem, QueueStatus } from './types';

/** 'On Hold' is reached through the hold/resume actions, never a status tap — see api.ts. */
type SettableQueueStatus = Exclude<QueueStatus, 'On Hold'>;

/**
 * "Start consult" / "Continue" — moves a queue row to a new status.
 *
 * Optimistic: the row is patched in every cached queue list immediately
 * (setQueriesData with the today() prefix matches every variant of this
 * hook's key — plain and patient-filtered alike — regardless of the
 * branchVersion/patientId segments useTodayQueue appends). On failure the
 * patch is rolled back and the real error surfaces to the caller; on success
 * the row is replaced with the server's response so token/timestamp fields
 * that only the server computes (consultation_started_at, a newly-minted
 * token) land correctly rather than staying at their optimistic guess.
 *
 * No auto-retry — see src/lib/queryClient.ts. A transparent retry of a status
 * PATCH that actually succeeded server-side but timed out in transit would
 * risk double-stamping consultation_started_at's COALESCE guard is harmless
 * here specifically, but the blanket no-retry policy is deliberately uniform
 * across every mutation rather than reasoned about per-endpoint each time.
 */
export function useUpdateQueueStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status, outOfOrderReason }: { id: number; status: SettableQueueStatus; outOfOrderReason?: string }) =>
      updateQueueStatus(id, status, outOfOrderReason),

    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: keys.queue.today() });

      const previous = queryClient.getQueriesData<QueueItem[]>({ queryKey: keys.queue.today() });

      queryClient.setQueriesData<QueueItem[]>({ queryKey: keys.queue.today() }, (old) =>
        old?.map((item) => (item.id === id ? { ...item, status } : item))
      );

      return { previous };
    },

    onError: (_err, _vars, context) => {
      context?.previous.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },

    onSuccess: (updated) => {
      queryClient.setQueriesData<QueueItem[]>({ queryKey: keys.queue.today() }, (old) =>
        old?.map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
      );
    },
  });
}
